import { useContext, useMemo } from "react"
import { ErrorContext } from "@app/components/layout/error-snackbar"
import { appConstants } from "@app/core/appConstants"
import { SupportForm } from "@app/core/dto/support/support-form"
import { MyUserUpdateDto } from "@app/core/dto/user/my-user-update-dto"
import { UserCreationFromInvitationDto } from "@app/core/dto/user/user-creation-from-invitation-dto"
import { User } from "@app/core/dto/user/user"
import { UserForm } from "@app/core/dto/user/user-form"
import { UserUpdateDto } from "@app/core/dto/user/user-update-dto"
import { resolveUrl } from "@app/core/services/http-service"
import { useHttp } from "./use-http"

interface UserHook {
  getCurrentUser(): Promise<User>
  adminGetUser(userId: string): Promise<User>
  updateMyUser(userUpdateDto: MyUserUpdateDto): Promise<User>
  adminUpdateUser(userUpdateDto: UserUpdateDto): Promise<Response>
  createUserFromInvitation(user: UserCreationFromInvitationDto, invitationId: string): Promise<void>
  sendInvitation(invitation: UserForm): Promise<void>
  sendAdminInvitation(organizationId: string, invitation: UserForm): Promise<void>
  sendSupportForm(supportForm: SupportForm): Promise<void>
  sendConfirmationAccountEmail(username: string): Promise<void>
  disableUser(cognitoUserId: string): Promise<Response>
  adminDisableUser(cognitoUserId: string, organizationId: string): Promise<Response>
  /** US 2419: true when the collaborator created his account but never entered the confirmation code. */
  getIsCollaboratorPendingConfirmation(cognitoUserId: string): Promise<boolean>
  /** US 2419: sends a new confirmation code to a collaborator whose account is still unconfirmed. */
  resendConfirmationCode(cognitoUserId: string): Promise<void>
  getIsBimModelTutorialEnabled(): Promise<any>
  disableBimModelTutorial(): Promise<Response>
  cancelUserOrganisationInvitation(email: string, organizationProjectId: string): Promise<Response>
  sendUserConnexionInformation(): Promise<Response>
  changePropectOption(userId: string): Promise<void>
}

export function useUser(): UserHook {
  const openErrorSnackbar = useContext(ErrorContext)
  const { get, post, put, deleteRequest } = useHttp()

  return useMemo(
    () => ({
      getCurrentUser(): Promise<User> {
        return get(appConstants.apiEndpoints.USER, []).then((response) => {
          if (response.status !== 404) {
            return response.json()
          } else {
            throw new Error("USER_EXITS_COGNITO_BUT_DOES_NOT_EXISTS_IN_DB")
          }
        })
      },
      adminGetUser(userId: string): Promise<User> {
        return get(`${appConstants.apiEndpoints.USER}/admin/${userId}`, []).then((response) => response.json())
      },
      updateMyUser(userUpdateDto: MyUserUpdateDto): Promise<User> {
        return put(appConstants.apiEndpoints.USER, userUpdateDto).then((response) => response.json())
      },
      adminUpdateUser(userUpdateDto: UserUpdateDto): Promise<Response> {
        return put(`${appConstants.apiEndpoints.USER}/admin`, userUpdateDto)
      },
      createUserFromInvitation(user: UserCreationFromInvitationDto, invitationId: string): Promise<void> {
        return post(`${appConstants.apiEndpoints.USER}/create-user`, user, [
          {
            key: "invitationId",
            value: invitationId,
          },
        ]).then(() => undefined)
      },
      sendInvitation(invitation: UserForm): Promise<void> {
        return post(appConstants.apiEndpoints.USER_SEND_INVITATION, invitation).then(() => undefined)
      },
      sendAdminInvitation(organizationId: string, invitation: UserForm): Promise<void> {
        return post(`${appConstants.apiEndpoints.USER}/send-admin-invitation`, invitation, [
          { key: "organizationId", value: organizationId },
        ]).then(() => undefined)
      },
      sendSupportForm(supportForm: SupportForm): Promise<void> {
        return post(`${appConstants.apiEndpoints.USER}/send-support-form`, supportForm).then(() => undefined)
      },
      sendConfirmationAccountEmail(userEmail: string): Promise<void> {
        return post(`${appConstants.apiEndpoints.USER}/send-confirmation-email-account`, {}, [
          { key: "userEmail", value: userEmail.toLowerCase() },
        ]).then()
      },
      disableUser(cognitoUserId: string): Promise<Response> {
        return deleteRequest(resolveUrl(appConstants.apiEndpoints.USER_WITH_ID, [cognitoUserId]))
      },
      adminDisableUser(cognitoUserId: string, organizationId: string): Promise<Response> {
        return deleteRequest(resolveUrl(appConstants.apiEndpoints.USER_ADMIN, [cognitoUserId]), [
          { key: "organizationId", value: organizationId },
        ])
      },
      getIsCollaboratorPendingConfirmation(cognitoUserId: string): Promise<boolean> {
        return get(`${appConstants.apiEndpoints.USER}/${cognitoUserId}/pending-confirmation`).then((response) =>
          response.ok ? response.json() : false
        )
      },
      resendConfirmationCode(cognitoUserId: string): Promise<void> {
        return post(`${appConstants.apiEndpoints.USER}/${cognitoUserId}/resend-confirmation-code`, {}).then(() => undefined)
      },
      getIsBimModelTutorialEnabled(): Promise<any> {
        return get(`${appConstants.apiEndpoints.USER}/tutorials-enabled`).then((response) => {
          if (response.status === 404) {
            openErrorSnackbar(new Error("Impossible de savoir si vous avez demandé de ne plus afficher le tutoriel"))
            return { bimModelTutorialEnabled: true }
          } else {
            return response.json()
          }
        })
      },
      disableBimModelTutorial(): Promise<Response> {
        return put(`${appConstants.apiEndpoints.USER}/disable-bim-model-tutorial`, {}).then((response) => response.json())
      },
      cancelUserOrganisationInvitation(email: string, organizationId: string): Promise<Response> {
        return post(`${appConstants.apiEndpoints.USER}/cancel-invitation-on-organization`, {}, [
          {
            key: "email",
            value: email.toLowerCase(),
          },
          {
            key: "organizationId",
            value: organizationId,
          },
        ])
      },
      sendUserConnexionInformation(): Promise<Response> {
        return put(`${appConstants.apiEndpoints.USER}/connexion`, {}, [])
      },
      changePropectOption(userId: string): Promise<void> {
        return post(`${appConstants.apiEndpoints.USER}/prospect-option`, {}, [
          {
            key: "userId",
            value: userId,
          },
        ]).then(() => undefined)
      },
    }),
    [post, openErrorSnackbar, deleteRequest, get, put]
  )
}
